import { CreateAppendMessage } from '@assistant-ui/react'

import type { PendingMessageSession } from '@/components/chat-v2/lib/pendingOptimisticSession'
import type {
  Attachment,
  PastedTextCard,
  ToolType,
} from '@/components/chat-v2/providers/ChatEnvironmentProvider'

import type { AgentName } from '@/types/chat-v2'
import type { UUID } from '@/types/Id'

// region: safe storage access

/**
 * `window.localStorage` behind try/catch: quota and private-mode failures
 * degrade to reads of nothing and dropped writes instead of throwing.
 * Client-only — callers must not touch it during SSR.
 */
export const safeLocalStorage = {
  getItem: (name: string): string | null => {
    try {
      return window.localStorage.getItem(name)
    } catch {
      return null
    }
  },
  setItem: (name: string, value: string): void => {
    try {
      window.localStorage.setItem(name, value)
    } catch {
      // Callers keep their in-memory state; it just won't survive a reload.
    }
  },
  removeItem: (name: string): void => {
    try {
      window.localStorage.removeItem(name)
    } catch {
      // Nothing to clean up if storage is unavailable.
    }
  },
}

// endregion: safe storage access

// region: pending message

const PENDING_MESSAGE_KEY_PREFIX = 'pending_message_'
const PENDING_MESSAGE_MAX_AGE_MS = 30_000
const PENDING_MESSAGE_VERSION = 1

export interface SavedPendingMessage<SessionT = PendingMessageSession> {
  version: typeof PENDING_MESSAGE_VERSION
  sessionId: UUID
  message: CreateAppendMessage
  attachments: Attachment[]
  modelName?: string
  selectedIntegrations?: string[]
  optimisticSession?: SessionT
  savedAt: number
}

type SavePendingMessageArgs<SessionT = PendingMessageSession> = Omit<
  SavedPendingMessage<SessionT>,
  'savedAt' | 'version'
>

export const savePendingMessage = <SessionT = PendingMessageSession>(
  args: SavePendingMessageArgs<SessionT>,
): void => {
  try {
    const key = `${PENDING_MESSAGE_KEY_PREFIX}${args.sessionId}`
    localStorage.setItem(
      key,
      JSON.stringify({
        ...args,
        version: PENDING_MESSAGE_VERSION,
        savedAt: Date.now(),
      } satisfies SavedPendingMessage<SessionT>),
    )
  } catch (error) {
    console.warn(
      `Failed to save pending message to localStorage for sessionId: ${args.sessionId}`,
      error,
    )
  }
}

interface GetPendingMessageArgs {
  sessionId: UUID
}

export const getPendingMessage = <SessionT = PendingMessageSession>(
  args: GetPendingMessageArgs,
): SavedPendingMessage<SessionT> | null => {
  try {
    const key = `${PENDING_MESSAGE_KEY_PREFIX}${args.sessionId}`
    const stored = localStorage.getItem(key)
    if (!stored) return null

    const parsed = JSON.parse(stored) as SavedPendingMessage<SessionT>
    if (
      parsed.version !== PENDING_MESSAGE_VERSION ||
      Date.now() - parsed.savedAt > PENDING_MESSAGE_MAX_AGE_MS
    ) {
      localStorage.removeItem(key)
      return null
    }

    return parsed
  } catch (error) {
    console.warn(
      `Failed to retrieve pending message from localStorage for sessionId: ${args.sessionId}`,
      error,
    )
    return null
  }
}

interface RemovePendingMessageArgs {
  sessionId: UUID
}

export const removePendingMessage = ({
  sessionId,
}: RemovePendingMessageArgs): void => {
  try {
    const key = `${PENDING_MESSAGE_KEY_PREFIX}${sessionId}`
    localStorage.removeItem(key)
  } catch (error) {
    console.warn(
      `Failed to remove pending message from localStorage for sessionId: ${sessionId}`,
      error,
    )
  }
}

export const clearAllPendingMessages = (): void => {
  try {
    const keys = Object.keys(localStorage).filter((key) =>
      key.startsWith(PENDING_MESSAGE_KEY_PREFIX),
    )
    keys.forEach((key) => localStorage.removeItem(key))
  } catch (error) {
    console.warn('Failed to clear pending messages from localStorage:', error)
  }
}

// endregion: pending message

// region: pending prompt submission

const PENDING_PROMPT_KEY = 'sweetspot:pending_prompt_submission'

const PENDING_PROMPT_MAX_AGE_MS = 30_000

interface PendingPromptSubmission {
  promptId: UUID
  content: string
  title?: string
  savedAt: number
}

export const savePendingPromptSubmission = (
  args: Omit<PendingPromptSubmission, 'savedAt'>,
): void => {
  try {
    const entry: PendingPromptSubmission = { ...args, savedAt: Date.now() }
    localStorage.setItem(PENDING_PROMPT_KEY, JSON.stringify(entry))
  } catch (error) {
    console.warn('Failed to save pending prompt submission:', error)
  }
}

export const getPendingPromptSubmission =
  (): PendingPromptSubmission | null => {
    try {
      const stored = localStorage.getItem(PENDING_PROMPT_KEY)
      if (!stored) return null
      const parsed: PendingPromptSubmission = JSON.parse(stored)
      if (Date.now() - parsed.savedAt > PENDING_PROMPT_MAX_AGE_MS) {
        localStorage.removeItem(PENDING_PROMPT_KEY)
        return null
      }
      return parsed
    } catch (error) {
      console.warn('Failed to retrieve pending prompt submission:', error)
      return null
    }
  }

export const removePendingPromptSubmission = (): void => {
  try {
    localStorage.removeItem(PENDING_PROMPT_KEY)
  } catch (error) {
    console.warn('Failed to remove pending prompt submission:', error)
  }
}

// endregion: pending prompt submission

// region: pending chat auto-send

export const PENDING_CHAT_AUTO_SEND_KEY = 'sweetspot:pending_chat_auto_send'

// The channel's pre-rename key (playbook generation). Read as a fallback so a
// handoff saved by an old bundle mid-deploy still fires; removal clears both.
const LEGACY_PENDING_CHAT_AUTO_SEND_KEY =
  'sweetspot:pending_playbook_generation'

const PENDING_CHAT_AUTO_SEND_MAX_AGE_MS = 30_000

interface PendingChatAutoSend {
  /** Full chat message to send immediately on arrival — the user never edits
   * it (playbook generation, the monitors set-up CTA, …). */
  message: string
  /** Org + agent the message targets; consumers must only send on a matching surface. */
  orgId: UUID
  agentName: AgentName
  savedAt: number
}

export const savePendingChatAutoSend = (
  args: Omit<PendingChatAutoSend, 'savedAt'>,
): void => {
  try {
    const entry: PendingChatAutoSend = { ...args, savedAt: Date.now() }
    localStorage.setItem(PENDING_CHAT_AUTO_SEND_KEY, JSON.stringify(entry))
  } catch (error) {
    console.warn('Failed to save pending chat auto-send:', error)
  }
}

const readPendingChatAutoSend = (key: string): PendingChatAutoSend | null => {
  const stored = localStorage.getItem(key)
  if (!stored) return null
  const parsed: PendingChatAutoSend = JSON.parse(stored)
  if (Date.now() - parsed.savedAt > PENDING_CHAT_AUTO_SEND_MAX_AGE_MS) {
    localStorage.removeItem(key)
    return null
  }
  return parsed
}

export const getPendingChatAutoSend = (): PendingChatAutoSend | null => {
  try {
    return (
      readPendingChatAutoSend(PENDING_CHAT_AUTO_SEND_KEY) ??
      readPendingChatAutoSend(LEGACY_PENDING_CHAT_AUTO_SEND_KEY)
    )
  } catch (error) {
    console.warn('Failed to retrieve pending chat auto-send:', error)
    return null
  }
}

export const removePendingChatAutoSend = (): void => {
  try {
    localStorage.removeItem(PENDING_CHAT_AUTO_SEND_KEY)
    localStorage.removeItem(LEGACY_PENDING_CHAT_AUTO_SEND_KEY)
  } catch (error) {
    console.warn('Failed to remove pending chat auto-send:', error)
  }
}

// endregion: pending chat auto-send

// region: pending chat prefill

const PENDING_CHAT_PREFILL_KEY = 'sweetspot:pending_chat_prefill'

const PENDING_CHAT_PREFILL_MAX_AGE_MS = 30_000

interface PendingChatPrefill {
  /** Composer text to prefill on the new-chat page — NOT auto-sent; the user
   * reviews/edits and sends it themselves. */
  content: string
  /** Chat tool to pre-select alongside the prefill (e.g. the discovery
   * announcement CTA selects Deep Research). */
  tool?: ToolType
  savedAt: number
}

export const savePendingChatPrefill = (
  args: Omit<PendingChatPrefill, 'savedAt'>,
): void => {
  try {
    const entry: PendingChatPrefill = { ...args, savedAt: Date.now() }
    localStorage.setItem(PENDING_CHAT_PREFILL_KEY, JSON.stringify(entry))
  } catch (error) {
    console.warn('Failed to save pending chat prefill:', error)
  }
}

export const getPendingChatPrefill = (): PendingChatPrefill | null => {
  try {
    const stored = localStorage.getItem(PENDING_CHAT_PREFILL_KEY)
    if (!stored) return null
    const parsed: PendingChatPrefill = JSON.parse(stored)
    if (Date.now() - parsed.savedAt > PENDING_CHAT_PREFILL_MAX_AGE_MS) {
      localStorage.removeItem(PENDING_CHAT_PREFILL_KEY)
      return null
    }
    return parsed
  } catch (error) {
    console.warn('Failed to retrieve pending chat prefill:', error)
    return null
  }
}

export const removePendingChatPrefill = (): void => {
  try {
    localStorage.removeItem(PENDING_CHAT_PREFILL_KEY)
  } catch (error) {
    console.warn('Failed to remove pending chat prefill:', error)
  }
}

// endregion: pending chat prefill

// region: pending chat cards

const PENDING_CHAT_CARDS_KEY = 'sweetspot:pending_chat_cards'

const PENDING_CHAT_CARDS_MAX_AGE_MS = 30_000

interface PendingChatCards {
  /** Cards to stage in a new chat's composer (plays picked on a map outside
   * a chat) — NOT sent; the user writes the message around them. */
  cards: PastedTextCard[]
  /** Org whose new chat takes the cards; another org's page leaves them. */
  orgId: UUID
  savedAt: number
}

export const savePendingChatCards = (
  args: Omit<PendingChatCards, 'savedAt'>,
): void => {
  try {
    const entry: PendingChatCards = { ...args, savedAt: Date.now() }
    localStorage.setItem(PENDING_CHAT_CARDS_KEY, JSON.stringify(entry))
  } catch (error) {
    console.warn('Failed to save pending chat cards:', error)
  }
}

export const getPendingChatCards = (): PendingChatCards | null => {
  try {
    const stored = localStorage.getItem(PENDING_CHAT_CARDS_KEY)
    if (!stored) return null
    const parsed: PendingChatCards = JSON.parse(stored)
    if (Date.now() - parsed.savedAt > PENDING_CHAT_CARDS_MAX_AGE_MS) {
      localStorage.removeItem(PENDING_CHAT_CARDS_KEY)
      return null
    }
    return parsed
  } catch (error) {
    console.warn('Failed to retrieve pending chat cards:', error)
    return null
  }
}

export const removePendingChatCards = (): void => {
  try {
    localStorage.removeItem(PENDING_CHAT_CARDS_KEY)
  } catch (error) {
    console.warn('Failed to remove pending chat cards:', error)
  }
}

// endregion: pending chat cards
